/**
 * `navigator.geolocation.watchPosition`, started only by an explicit tap.
 *
 * The 📍 button in the composer is a one-shot outside the navigation mode and a
 * TOGGLE inside it (design §3.4). Nothing here starts on its own, and that is
 * not only politeness: on iOS the permission prompt only appears in response to
 * a gesture, so an automatic restart after an F5 silently does nothing — which
 * is why the overlay offers «Продолжить вести» instead of pretending.
 *
 * `maximumAge: 5000` rather than the composer's `0`: a continuous watch that
 * refuses every cached reading spins the radio for no accuracy the walk can
 * use, and on Android that is measured in battery.
 *
 * A lost signal is NOT a stop. `TIMEOUT` and `POSITION_UNAVAILABLE` keep the
 * last fix on screen, say so, and retry — only `PERMISSION_DENIED` ends the
 * watch, because retrying a refusal is asking again.
 */

import { useEffect, useRef, useState } from 'react';

import { useSession } from '../store/sessionStore';
import { useNav } from './navStore';
import type { Fix } from './track';

export type WatchState = 'idle' | 'asking' | 'watching' | 'denied' | 'unavailable';

const OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 5000,
  timeout: 20_000,
};

/** How long a lost signal is left alone before the watch is restarted. */
const RETRY_MS = 30_000;

export function useWatch(enabled: boolean): { state: WatchState; last: Fix | null } {
  const [state, setState] = useState<WatchState>('idle');
  const [last, setLast] = useState<Fix | null>(null);
  const retry = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) {
      setState('idle');
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setState('unavailable');
      return;
    }

    let watchId: number | null = null;
    let cancelled = false;

    const start = () => {
      if (cancelled) return;
      setState((current) => (current === 'watching' ? current : 'asking'));
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          if (cancelled) return;
          setState('watching');
          const fix: Fix = {
            at: position.timestamp,
            lng: position.coords.longitude,
            lat: position.coords.latitude,
            accuracyM: position.coords.accuracy,
            headingDeg: Number.isFinite(position.coords.heading)
              ? (position.coords.heading as number)
              : null,
            speedMs: Number.isFinite(position.coords.speed)
              ? (position.coords.speed as number)
              : null,
          };
          setLast(fix);
          // The run already carries coordinates (`App.tsx`), so keeping this
          // fresh is the whole of «the guide knows where you are» — no extra
          // request anywhere.
          useSession.getState().setGeo({ lat: fix.lat, lon: fix.lng }, 'granted');
        },
        (error) => {
          if (cancelled) return;
          if (error.code === error.PERMISSION_DENIED) {
            setState('denied');
            useSession.getState().setGeo(null, 'denied');
            // The switch has to come back UP. It is the visitor's stated
            // intent, and leaving it on over a refused permission left the
            // composer's 📍 pressed and captioned «веду по маршруту» while the
            // overlay said access was closed — two answers to one question.
            // `geoState` is what remembers the refusal; this is only the switch.
            useNav.getState().setWatching(false);
            return;
          }
          // Lost, not refused: keep the last fix and come back for more.
          setState('asking');
          if (watchId !== null) navigator.geolocation.clearWatch(watchId);
          watchId = null;
          retry.current = setTimeout(start, RETRY_MS);
        },
        OPTIONS,
      );
    };

    start();

    return () => {
      cancelled = true;
      if (retry.current) clearTimeout(retry.current);
      retry.current = null;
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
    };
  }, [enabled]);

  return { state, last };
}
